import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Image, Modal, Tag, Skeleton, message } from 'antd';
import { EyeOutlined, HomeOutlined, LeftOutlined, RightOutlined, PhoneOutlined } from '@ant-design/icons';
import './CaseStudiesPage.css';
import apiClient from '../utils/api';

interface CaseStudy {
  id: number;
  title: string;
  location: string;
  imageUrl: string;
  images: string[];
  description: string;
  content?: string;
  store_name: string;
  store_avatar: string;
  store_phone: string;
  create_time: string;
  region_id?: number;
}

interface ApiResponse {
  code: number;
  msg: string;
  data: {
    list: CaseStudy[];
    total: number;
  };
}

// 随机打乱数组
const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const CaseStudiesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [caseList, setCaseList] = useState<CaseStudy[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseStudy | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const casesPerPage = 6;

  const fetchCaseList = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<ApiResponse>('/index.php?s=/api/goods/decoration_case_list');
      if (response.data.code === 1) {
        const shuffledList = shuffleArray(response.data.data.list || []);
        setCaseList(shuffledList);
      } else {
        message.error(response.data.msg || '获取案例列表失败');
      }
    } catch (error) {
      console.error('获取案例列表失败:', error);
      message.error('获取案例列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseList();
  }, []);

  // 获取当前页的案例
  const getCurrentPageCases = () => {
    const startIndex = currentPage * casesPerPage;
    const endIndex = startIndex + casesPerPage;
    return caseList.slice(startIndex, endIndex);
  };

  const currentCases = getCurrentPageCases();

  // 分页切换函数
  const handlePageChange = (direction: 'prev' | 'next') => {
    const totalPages = Math.ceil(caseList.length / casesPerPage);
    if (totalPages === 0) return;

    setCurrentPage((prev) => {
      if (direction === 'prev') {
        return prev === 0 ? totalPages - 1 : prev - 1;
      } else {
        return (prev + 1) % totalPages;
      }
    });
  };

  const handleCaseClick = (caseStudy: CaseStudy) => {
    setSelectedCase(caseStudy);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const handleCall = (phone: string) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const renderCaseCard = (caseStudy: CaseStudy, index: number) => (
    <div 
      key={caseStudy.id}
      className="case-card-item"
      onClick={() => handleCaseClick(caseStudy)}
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <div className="case-card-design">
        <div className="card-image-wrapper">
          <div className="card-image-aspect">
            <Image
              src={caseStudy.imageUrl}
              alt={caseStudy.title}
              className="card-image"
              preview={false}
              placeholder={
                <div className="image-placeholder">
                  <Skeleton.Image active />
                </div>
              }
              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L295aSx6LSl5Zu+54mHPC90ZXh0Pjwvc3ZnPg=="
            />
          </div>
          <div className="card-overlay">
            <div className="view-btn">
              <EyeOutlined />
              <span>查看详情</span>
            </div>
          </div>
          {caseStudy.location && (
            <div className="card-badge">
              <HomeOutlined />
              <span>{caseStudy.location}</span>
            </div>
          )}
        </div>
        <div className="card-content">
          <h3 className="card-title">{caseStudy.title}</h3>
          <div className="card-meta">
            <div className="store-info">
              {caseStudy.store_avatar && (
                <img src={caseStudy.store_avatar} alt="" className="store-avatar" />
              )}
              <span className="store-name">{caseStudy.store_name}</span>
            </div>
            <span className="case-date">{caseStudy.create_time}</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="case-page">
        <div className="case-container">
          <p className="page-subtitle">品质装修，匠心呈现</p>
          <div className="loading-wrapper">
            <div className="loading-row">
              {[1, 2, 3].map((i) => (
                <div key={i} className="loading-card">
                  <Skeleton.Image active style={{ width: '100%', height: 260 }} />
                  <div style={{ padding: 20 }}>
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (caseList.length === 0) {
    return (
      <div className="case-page">
        <div className="case-container">
          <p className="page-subtitle">品质装修，匠心呈现</p>
          <div className="empty-state">
            <div className="empty-icon">🏠</div>
            <p>暂无装修案例</p>
            <span>精彩案例正在准备中，敬请期待</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="case-page">
      <div className="case-container">
        {/* 页面标题 */}
        <p className="page-subtitle">品质装修，匠心呈现</p>

        {/* 桌面端案例展示区域 - 网格布局（分页显示6个） */}
        {currentCases.length > 0 && (
          <div className="case-grid-section desktop-only">
            {/* 左导航按钮 */}
            <div className="case-nav-left">
              <button
                className="nav-btn nav-btn-left"
                onClick={() => handlePageChange('prev')}
                aria-label="上一页"
              >
                <LeftOutlined />
              </button>
            </div>

            {/* 卡片网格区域 */}
            <div className="case-grid-content">
              {/* 第一行 - 3个卡片 */}
              <div className="case-grid-row">
                {currentCases.slice(0, 3).map((caseStudy, index) => renderCaseCard(caseStudy, index))}
              </div>

              {/* 第二行 - 3个卡片 */}
              <div className="case-grid-row">
                {currentCases.slice(3, 6).map((caseStudy, index) => renderCaseCard(caseStudy, index + 3))}
              </div>
            </div>

            {/* 右导航按钮 */}
            <div className="case-nav-right">
              <button
                className="nav-btn nav-btn-right"
                onClick={() => handlePageChange('next')}
                aria-label="下一页"
              >
                <RightOutlined />
              </button>
            </div>
          </div>
        )}

        {/* 移动端案例展示区域 - 2列布局显示所有案例 */}
        {caseList.length > 0 && (
          <div className="case-grid-section mobile-only">
            <div className="case-grid-content mobile">
              <div className="case-grid-row mobile">
                {caseList.map((caseStudy, index) => renderCaseCard(caseStudy, index))}
              </div>
            </div>
          </div>
        )}

        {/* 案例详情弹窗 */}
        <Modal
          title={null}
          open={modalVisible}
          onCancel={handleModalClose}
          footer={null}
          width={900}
          className="case-detail-modal"
          destroyOnClose
        >
          {selectedCase && (
            <div className="detail-content">
              <div className="detail-header">
                <div className="detail-store">
                  {selectedCase.store_avatar && (
                    <img src={selectedCase.store_avatar} alt="" className="detail-store-avatar" />
                  )}
                  <div className="detail-store-info">
                    <h3>{selectedCase.store_name}</h3>
                    <span>{selectedCase.create_time}</span>
                  </div>
                </div>
                {selectedCase.store_phone && (
                  <button 
                    className="call-btn"
                    onClick={() => handleCall(selectedCase.store_phone)}
                  >
                    <PhoneOutlined />
                    <span>联系商家</span>
                  </button>
                )}
              </div>
              
              <div className="detail-gallery">
                <Image.PreviewGroup>
                  {selectedCase.images.length > 0 ? (
                    <div className="gallery-grid">
                      {selectedCase.images.map((img, index) => (
                        <div key={index} className={`gallery-item ${index === 0 ? 'gallery-item-main' : ''}`}>
                          <Image
                            src={img}
                            alt={`案例图片 ${index + 1}`}
                            className="gallery-image"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="gallery-main">
                      <Image
                        src={selectedCase.imageUrl}
                        alt={selectedCase.title}
                        className="gallery-image"
                      />
                    </div>
                  )}
                </Image.PreviewGroup>
              </div>
              
              <div className="detail-body">
                <div className="detail-tags">
                  {selectedCase.location && (
                    <Tag icon={<HomeOutlined />} color="orange">
                      {selectedCase.location}
                    </Tag>
                  )}
                  <Tag color="blue">{selectedCase.store_name}</Tag>
                  <Tag color="green">{selectedCase.create_time}</Tag>
                </div>
                <h2 className="detail-title">{selectedCase.title}</h2>
                
                {/* 案例详情内容 */}
                <div className="detail-content-section">
                  {selectedCase.content ? (
                    <div 
                      className="detail-content-html"
                      dangerouslySetInnerHTML={{ __html: selectedCase.content.replace(/<video[^>]*>.*?<\/video>/gi, '').replace(/<source[^>]*>/gi, '') }}
                    />
                  ) : selectedCase.description ? (
                    <p className="detail-desc">{selectedCase.description}</p>
                  ) : (
                    <p className="detail-desc detail-desc-empty">暂无案例详情</p>
                  )}
                </div>
                
                {/* 图片列表（如果有更多图片） */}
                {selectedCase.images.length > 1 && (
                  <div className="detail-images-section">
                    <h4 className="section-title">案例实拍</h4>
                    <div className="detail-images-list">
                      {selectedCase.images.map((img, index) => (
                        <div key={index} className="detail-image-item">
                          <Image
                            src={img}
                            alt={`案例图片 ${index + 1}`}
                            className="detail-image-thumb"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default CaseStudiesPage;
